import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faCheck,
  faGrip,
  faList,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { formatCurrency } from "../../../../services/api";
import noImagePlaceholder from "../../../../assets/img/not-image.png";
import styles from "./LotteryPicker.module.css";
import ComingSoonBadge from "../../../../components/ui/ComingSoonBadge";
import {
  isLotteryBettingClosed,
  getOpenClosingBadgeText,
  isLotteryComingSoon,
} from "../lotteryHelpers";

const GRID_PAGE_SIZE = 12;
const LIST_PAGE_SIZE = 4;
const VIEW_MODE_STORAGE_KEY = "gold_apuestas_lottery_view_mode";

function readStoredViewMode() {
  try {
    const value = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (value === "grid" || value === "list") return value;
  } catch {
    /* localStorage no disponible */
  }
  return "grid";
}

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const getLotteryImage = (lottery) => {
  const src = lottery.image || lottery.logo || lottery.img || lottery.imagen;
  return src?.trim() ? src : noImagePlaceholder;
};

const getJackpotAmount = (lottery) =>
  lottery.amount ?? lottery.prize_amount ?? lottery.premio;

const getJackpotLabel = (lottery) => {
  const amount = getJackpotAmount(lottery);
  if (amount == null || amount === "") return null;
  return formatCurrency(amount);
};

/**
 * @param {object} props
 * @param {Array} props.lotteries
 * @param {Array} [props.comingSoon] Loterías "Próximamente": visibles, en gris y NO seleccionables.
 * @param {string|number|null} props.selectedId
 * @param {(lottery: object) => void} props.onSelect
 * @param {React.Ref<HTMLInputElement>} [props.searchInputRef] Para enfocar el buscador desde "Cambiar".
 */
export default function LotteryPicker({
  lotteries = [],
  comingSoon = [],
  selectedId,
  onSelect,
  now,
  searchInputRef,
}) {
  const [query, setQuery] = useState("");
  const [viewMode, setViewMode] = useState(readStoredViewMode);

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);
  const [page, setPage] = useState(0);
  const [brokenImages, setBrokenImages] = useState({});

  // Activas primero; las "Próximamente" siempre al final.
  const allLotteries = useMemo(
    () => [
      ...lotteries.filter((lottery) => !isLotteryComingSoon(lottery)),
      ...lotteries.filter(isLotteryComingSoon),
      ...comingSoon.map((lottery) => ({ ...lottery, coming_soon: true })),
    ],
    [lotteries, comingSoon]
  );

  const filtered = useMemo(() => {
    const q = normalizeText(query.trim());
    if (!q) return allLotteries;
    return allLotteries.filter((lottery) =>
      normalizeText(lottery.name).includes(q)
    );
  }, [allLotteries, query]);

  const pageSize = viewMode === "grid" ? GRID_PAGE_SIZE : LIST_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  useEffect(() => {
    setPage(0);
  }, [query, viewMode]);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  const pageItems = useMemo(() => {
    const start = page * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const listClassName =
    viewMode === "grid" ? styles.lotteryGrid : styles.lotteryList;

  const renderLogoImg = (lottery, className) => (
    <img
      src={brokenImages[lottery.id] ? noImagePlaceholder : getLotteryImage(lottery)}
      alt=""
      className={className}
      onError={() =>
        setBrokenImages((prev) => ({
          ...prev,
          [lottery.id]: true,
        }))
      }
    />
  );

  /** Tarjeta "Próximamente": no es botón (no enfocable, no dispara onSelect). */
  const renderComingSoonCard = (lottery) => {
    const jackpot = getJackpotLabel(lottery);
    const isGrid = viewMode === "grid";
    return (
      <li key={`soon-${lottery.id}`}>
        <div
          className={`${styles.card} ${isGrid ? styles.cardGrid : styles.cardList} ${
            styles.cardSoon
          }`}
          aria-disabled="true"
          title={`${lottery.name} · Próximamente`}
        >
          <ComingSoonBadge size="sm" className={styles.soonBadge} />
          {isGrid ? (
            <>
              <div className={styles.gridLogoWrap}>
                {renderLogoImg(lottery, styles.gridLogo)}
              </div>
              <p className={styles.gridName}>{lottery.name}</p>
              {jackpot && <p className={styles.gridPrize}>{jackpot}</p>}
            </>
          ) : (
            <>
              <div className={styles.logoWrap}>
                {renderLogoImg(lottery, styles.logo)}
              </div>
              <div className={styles.cardBody}>
                <p className={styles.cardName}>{lottery.name}</p>
                {jackpot && (
                  <div className={styles.jackpotBlock}>
                    <span className={styles.jackpotLabel}>Premio mayor</span>
                    <p className={styles.jackpotValue}>{jackpot}</p>
                  </div>
                )}
                <span className={styles.soonInline}>Muy pronto podrás jugarla</span>
              </div>
            </>
          )}
        </div>
      </li>
    );
  };

  const renderGridCard = (lottery) => {
    const isSelected = String(lottery.id) === String(selectedId);
    const jackpot = getJackpotLabel(lottery);
    const imgBroken = brokenImages[lottery.id];
    const closed = isLotteryBettingClosed(lottery, now);
    const openBadge = getOpenClosingBadgeText(lottery, now);

    return (
      <li key={lottery.id}>
        <button
          type="button"
          className={`${styles.card} ${styles.cardGrid} ${
            isSelected ? styles.cardSelected : ""
          } ${closed ? styles.cardClosed : ""}`}
          onClick={() => {
            if (!closed) onSelect(lottery);
          }}
          disabled={closed}
          aria-pressed={isSelected}
          title={closed ? `${lottery.name} · Cerrada` : lottery.name}
        >
          {isSelected && (
            <span className={styles.selectedBadge} aria-hidden>
              <FontAwesomeIcon icon={faCheck} />
            </span>
          )}
          {closed ? (
            <span className={styles.closedBadge}>CERRADO</span>
          ) : null}
          <div className={styles.gridLogoWrap}>
            <img
              src={imgBroken ? noImagePlaceholder : getLotteryImage(lottery)}
              alt=""
              className={styles.gridLogo}
              onError={() =>
                setBrokenImages((prev) => ({
                  ...prev,
                  [lottery.id]: true,
                }))
              }
            />
          </div>
          <p className={styles.gridName}>{lottery.name}</p>
          {jackpot && <p className={styles.gridPrize}>{jackpot}</p>}
          {!closed && openBadge ? (
            <span className={styles.gridOpen}>{openBadge}</span>
          ) : null}
        </button>
      </li>
    );
  };

  const renderListCard = (lottery) => {
    const isSelected = String(lottery.id) === String(selectedId);
    const jackpot = getJackpotLabel(lottery);
    const imgBroken = brokenImages[lottery.id];
    const closed = isLotteryBettingClosed(lottery, now);
    const openBadge = getOpenClosingBadgeText(lottery, now);

    return (
      <li key={lottery.id}>
        <button
          type="button"
          className={`${styles.card} ${styles.cardList} ${
            isSelected ? styles.cardSelected : ""
          } ${closed ? styles.cardClosed : ""}`}
          onClick={() => {
            if (!closed) onSelect(lottery);
          }}
          disabled={closed}
          aria-pressed={isSelected}
        >
          {isSelected && (
            <span className={styles.selectedBadge} aria-hidden>
              <FontAwesomeIcon icon={faCheck} />
            </span>
          )}
          <div className={styles.logoWrap}>
            <img
              src={imgBroken ? noImagePlaceholder : getLotteryImage(lottery)}
              alt=""
              className={styles.logo}
              onError={() =>
                setBrokenImages((prev) => ({
                  ...prev,
                  [lottery.id]: true,
                }))
              }
            />
          </div>
          <div className={styles.cardBody}>
            <p className={styles.cardName}>{lottery.name}</p>
            {closed ? (
              <span className={styles.closedInline}>Cerrada</span>
            ) : openBadge ? (
              <span className={styles.openInline}>{openBadge}</span>
            ) : null}
            {jackpot && (
              <div className={styles.jackpotBlock}>
                <span className={styles.jackpotLabel}>Premio mayor</span>
                <p className={styles.jackpotValue}>{jackpot}</p>
              </div>
            )}
            <span className={styles.fraction}>
              Fracción{" "}
              {formatCurrency(
                lottery.fraction_value ?? lottery.fractionValue ?? 0
              )}
            </span>
            <span className={styles.cardCta}>
              {closed ? "Cerrada" : isSelected ? "Seleccionada" : "Jugar"}
            </span>
          </div>
        </button>
      </li>
    );
  };

  const renderCard = (lottery) => {
    if (isLotteryComingSoon(lottery)) return renderComingSoonCard(lottery);
    return viewMode === "grid" ? renderGridCard(lottery) : renderListCard(lottery);
  };

  return (
    <section className={styles.picker} aria-label="Seleccionar lotería">
      <div className={styles.pickerHeader}>
        <p className={styles.pickerStep}>Paso 1</p>
        <h2 className={styles.pickerTitle}>Elige tu lotería</h2>
        <p className={styles.pickerHint}>
          Busca por nombre y toca una tarjeta para jugar.
        </p>
      </div>

      <label className={styles.searchWrap}>
        <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.searchIcon} />
        <input
          ref={searchInputRef}
          type="search"
          className={styles.searchInput}
          placeholder="Buscar lotería…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Buscar lotería por nombre"
        />
      </label>

      <div className={styles.viewToolbar}>
        <span className={styles.viewLabel}>Vista</span>
        <div className={styles.viewToggle} role="group" aria-label="Tipo de vista">
          <button
            type="button"
            className={`${styles.viewBtn} ${
              viewMode === "grid" ? styles.viewBtnActive : ""
            }`}
            onClick={() => setViewMode("grid")}
            aria-pressed={viewMode === "grid"}
          >
            <FontAwesomeIcon icon={faGrip} />
            Cuadrícula
          </button>
          <button
            type="button"
            className={`${styles.viewBtn} ${
              viewMode === "list" ? styles.viewBtnActive : ""
            }`}
            onClick={() => setViewMode("list")}
            aria-pressed={viewMode === "list"}
          >
            <FontAwesomeIcon icon={faList} />
            Lista
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className={styles.emptySearch} role="status">
          {query.trim()
            ? `No encontramos loterías con «${query.trim()}».`
            : "No hay loterías disponibles en este momento."}
        </p>
      ) : (
        <>
          <ul className={listClassName}>{pageItems.map(renderCard)}</ul>

          {totalPages > 1 && (
            <nav className={styles.pagination} aria-label="Paginación de loterías">
              <button
                type="button"
                className={styles.pageBtn}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
                Anterior
              </button>
              <span className={styles.pageInfo}>
                Página {page + 1} de {totalPages}
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Siguiente
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </nav>
          )}
        </>
      )}
    </section>
  );
}
